import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
  memo,
} from 'react';
import { PieChart, Pie, Cell, Sector } from 'recharts';
import { ThemeContext } from 'styled-components';
import { format } from 'date-fns';
import { dateFormats } from '../../constants';
import * as s from './styled-visao-geral';
import { mockCircle } from './mock';
import Progress from './vertical-progress';

const VisaoGeral = memo(({ hideBars, center, update }) => {
  const [graphSize, setGraphSize] = useState({ height: 0, width: 0 });
  const [activeIndex, setActiveIndex] = useState();
  const [activePie, setActivePie] = useState(0);
  const [data] = useState(mockCircle);
  const [animating, setAnimating] = useState(true);
  const { colors } = useContext(ThemeContext);

  const status = [
    { label: 'desligada', color: colors.label, value: Math.random() * 100 },
    {
      label: 'funcionando',
      color: colors.lightgreen,
      value: Math.random() * 100,
    },
    { label: 'alarmado', color: colors.yellow, value: Math.random() * 100 },
    { label: 'alarme', color: colors.orange, value: Math.random() * 100 },
    { label: 'erro', color: colors.red, value: Math.random() * 100 },
  ];

  const dados = [
    { label: 'Vazão' },
    { label: 'Temperatura' },
    { label: 'Vibração' },
    { label: 'Energia Gerada' },
    { label: 'Pressão' },
    { label: 'Potência' },
    { label: 'Fator de serviço' },
    { label: 'Corrente' },
  ];

  const tags = [
    colors.red,
    colors.orange,
    colors.yellow,
    colors.lightgreen,
    colors.label,
  ];

  const onMouseEnter = (data, index) => !animating && setActiveIndex(index);

  const renderActiveShape = (props) => {
    const RADIAN = Math.PI / 180;
    const {
      cx,
      cy,
      midAngle,
      innerRadius,
      outerRadius,
      startAngle,
      endAngle,
      percent,
      fill,
    } = props;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const mx = cx + (outerRadius + 10) * cos;
    const my = cy + (outerRadius + 20) * sin;
    const ex = mx + (cos >= 0 ? 1 : -1) * 8;
    const ey = my;
    const textAnchor = cos >= 0 ? 'start' : 'end';

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
        <Sector
          cx={cx}
          cy={cy}
          startAngle={startAngle}
          endAngle={endAngle}
          innerRadius={outerRadius + 6}
          outerRadius={outerRadius + 10}
          fill={fill}
        />
        <text
          x={ex + (cos >= 0 ? 1 : -1) * 3}
          y={ey}
          dy={12}
          textAnchor={textAnchor}
          fill={colors.darkerblue}
          style={{
            fontWeight: 'bold',
            fontSize: '12px',
            position: 'absolute',
            zIndex: 12,
          }}
        >
          {`${(percent * 100).toFixed(1)}%`}
        </text>
      </g>
    );
  };

  useEffect(() => {
    window.addEventListener('resize', resize);
    resize();
    // forceUpdate();
    return () => window.removeEventListener('resize', resize);
  }, []);

  const resize = () => {
    setGraphSize({
      height: document.getElementById('dashboard-graphs-container')
        .clientHeight,
      width: document.getElementById('dashboard-graphs-container').clientWidth,
    });
  };

  const renderCustomLabel = useCallback(({ cx, cy, index }, title) => {
    if (index !== 0) return <svg />;
    return (
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        fill={colors.darkerblue}
        style={{
          fontSize:
            window.innerWidth > 1585
              ? '14px'
              : window.innerWidth > 880
              ? '14px'
              : '10px',
          fontWeight: 'bold',
        }}
      >
        {title}
      </text>
    );
  }, [colors.darkerblue]);

  return (
    <s.Container id="dashboard-media-geral-container">
      <s.HeaderLine>
        <div>
          <s.HeaderTitle>Visão geral dos equipamentos</s.HeaderTitle>
        </div>
        {tags.map((tag) => (
          <s.Tag key={tag} background={tag} />
        ))}
      </s.HeaderLine>
      <div
        style={{
          display: 'flex',
          justifyContent: center ? 'center' : 'space-between',
          maxHeight: '92%',
          height: '100%',
        }}
      >
        <s.GraphWrapper
          id="dashboard-graphs-container"
          full={window.innerWidth <= 1094 || hideBars}
        >
          {data &&
            dados.map(({ label }, index) => (
              <PieChart
                onMouseEnter={() => !animating && setActivePie(index)}
                key={label}
                width={graphSize.width / 4}
                height={graphSize.height / 2}
              >
                <Pie
                  data={data[index]}
                  style={{ overflow: 'visible' }}
                  innerRadius="75%"
                  outerRadius="90%"
                  fill={colors.card}
                  label={(props) => renderCustomLabel(props, label)}
                  labelLine={false}
                  paddingAngle={2}
                  dataKey="value"
                  legendType="square"
                  onAnimationEnd={() =>
                    index === dados.length - 1 && setAnimating(false)
                  }
                  onMouseEnter={onMouseEnter}
                  activeIndex={[index === activePie ? activeIndex : undefined]}
                  activeShape={renderActiveShape}
                >
                  {status.map(({ color }, i) => (
                    <Cell
                      key={`cell-${color}`}
                      fill={color}
                      strokeWidth={i === 0 ? 4 : i === 1 ? 2 : 1}
                      stroke="false"
                    />
                  ))}
                </Pie>
              </PieChart>
            ))}
        </s.GraphWrapper>
        {window.innerWidth >= 1094 && !hideBars && (
          <div style={{ display: 'flex', width: '25%' }}>
            {status.map(({ color, label, value }) => (
              <Progress key={label} color={color} label={label} value={value} />
            ))}
          </div>
        )}
      </div>
      {update && (
        <s.UpdateContainer>
          <s.Label color={colors.label}>Última atualização</s.Label>
          <s.Label color={colors.darkblue}>
            {format(new Date(), dateFormats.TIME_DATE_DASH)}
          </s.Label>
        </s.UpdateContainer>
      )}
    </s.Container>
  );
});

export default VisaoGeral;
